import {
    Component,
    DestroyRef,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    OnChanges,
    OnDestroy,
    OnInit,
    output,
    signal,
    SimpleChanges
} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {SalesOwnerService} from "../../../services/sales-owner.service";
import {NotificationService} from "../../../services/notification.service";
import {Subscription} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'app-search-sales-executive',
    imports: [ReactiveFormsModule, FormsModule],
    templateUrl: './search-sales-executive.component.html',
    standalone: true,
    styleUrl: './search-sales-executive.component.scss'
})
export class SearchSalesExecutive implements OnChanges, OnInit, OnDestroy {
    salesOwnerService = inject(SalesOwnerService);
    private elementRef = inject(ElementRef);
    private destroyRef = inject(DestroyRef);
    private notificationService = inject(NotificationService);

    salesOwnerName = input<string | undefined>(undefined);
    salesOwnerId = input<number | undefined>(undefined);
    deliveredQty = input<number>(-1);

    filterOption = input<string>('none');
    instanceId = input<string>('default');
    disabled = input<boolean>(false);
    isListVisible = signal<boolean>(false);
    resetTrigger = input<boolean>(false);
    required = input<boolean>(true);


    selectedSalesOwnerId = output<number>();
    salesOwnerStatus = output<boolean>();

    private subscription = new Subscription();

    searchTerms = signal({
        sales_owner_name: '',
        status: -1,
        items_per_page: -1,
        page_number: 1
    });

    filterValue = signal('');

    get salesOwners() {
        const owners = this.salesOwnerService.all().filter(salesPerson => salesPerson.salesOwnerActive);
        return owners;
    }

    constructor() {
        this.salesOwnerService.registerDropdown(this.instanceId());

        effect(() => {
            this.salesOwnerService
                .find(this.searchTerms(), true)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    error: (error) => console.error('Search error:', error),
                });
        });

        effect(() => {
            const newOwnerId = this.salesOwnerId();
            if (newOwnerId === null || newOwnerId === -1 || newOwnerId === undefined) {
                this.clearSelection();
                this.salesOwnerStatus.emit(false);
            } else if (newOwnerId > 0) {
                this.salesOwnerService.getById(String(newOwnerId), this.instanceId(), true).subscribe({
                    next: (response) => {
                        const salesOwner = response?.data;
                        if (salesOwner && salesOwner.salesOwnerStatus) {
                            this.onOptionSelected(newOwnerId!);
                            this.salesOwnerStatus.emit(salesOwner.salesOwnerStatus);
                        } else {
                            this.notificationService.set({
                                type: 'alert',
                                message: `The sales executive '${salesOwner?.salesOwnerName}' is inactive and cannot be assigned further.`,
                                timeout: 7000
                            });
                            this.salesOwnerStatus.emit(false);
                        }
                    },
                    error: (error) => {
                        this.notificationService.set({
                            type: 'error',
                            message: 'Failed to verify sales executive status.',
                            timeout: 5000
                        });
                        this.clearSelection();
                        this.salesOwnerStatus.emit(false);
                    }
                });
            }
        });

                                        effect(() => {
            if (this.resetTrigger()) {
                this.resetSearchInput();                
            }
        });
    }

    ngOnInit(): void {
        this.salesOwnerService.find(this.searchTerms(),true).subscribe();
        this.subscription.add(
            this.salesOwnerService.dropdownState$.subscribe(state => {
                if (state.instanceId !== this.instanceId()) {
                    this.isListVisible.set(false);
                }
            })
        );
        this.subscription.add(
            this.salesOwnerService.getActiveForInstance(this.instanceId()).subscribe(value => {
                this.filterValue.set(value?.salesOwnerName || '');
            })
        );
    }

    onInputChange(value: string): void {
        this.filterValue.set(value);
        this.searchTerms.set({
            ...this.searchTerms(),
            sales_owner_name: value,
        });

        if (value.trim() !== '') {
            this.isListVisible.set(true);
        } else {
            this.selectedSalesOwnerId.emit(-1);
            this.salesOwnerStatus.emit(false);
            this.clearSelection(); // This will also set isListVisible to false
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['salesOwnerId']) {
            const newId = changes['salesOwnerId'].currentValue;
            if (newId === -1 || newId === null || newId === undefined) {
                this.clearSelection();
                this.salesOwnerStatus.emit(false);
            }
        }
    }

    selectAll(event: MouseEvent): void {
        (event.target as HTMLInputElement).select();
    }

    onOptionSelected(id: number) {
        this.isListVisible.set(false);
        this.salesOwnerService.getById(String(id), this.instanceId(), true).subscribe({
            next: (salesOwner) => {
                this.filterValue.set(salesOwner?.data.salesOwnerName || '');
                this.selectedSalesOwnerId.emit(id);
                this.salesOwnerStatus.emit(salesOwner?.data.salesOwnerStatus ?? false); // Emit active status
            },
            error: (error) => {
                this.notificationService.set({
                    type: 'error',
                    message: 'Failed to fetch sales executive details.',
                    timeout: 5000
                });
                this.clearSelection();
                this.salesOwnerStatus.emit(false);
            }
        });
    }

    clearSelection(): void {
        this.filterValue.set('');
        this.selectedSalesOwnerId.emit(-1);
        this.salesOwnerStatus.emit(false);
        this.isListVisible.set(false);
        this.salesOwnerService.setActiveForInstance(this.instanceId(), undefined);
    }

    onInputFocus(): void {
        if (this.filterValue().trim() !== '') {
            this.isListVisible.set(true);
            this.salesOwnerService.setDropdownState(this.instanceId(), true);
        }
    }

    @HostListener('document:click', ['$event'])
    clickOutsideDropdown(event: any): void {
        if (!this.elementRef.nativeElement.contains(event.target) && this.isListVisible()) {
            this.isListVisible.set(false);
            this.salesOwnerService.setDropdownState(this.instanceId(), false);
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.salesOwnerService.unregisterDropdown(this.instanceId());
    }

    resetSearchInput = () => {
        this.clearSelection();
        this.selectedSalesOwnerId.emit(-1);
        this.salesOwnerStatus.emit(false);
    };
}